import React, { FC } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import { Button, Form, ReactSelectOverrides } from '@bphxd/ds-core-react';
import { TCertificate } from '../../../lib/types';
import { useQuery } from '@apollo/client';
import { GET_CERTIFICATE_DETAILS } from '../../../graph-queries/tar/certificate';
import AttachmentsList from '../../organisms/CertificateAttachments/AttachmentsList';
import { useCertificateAttachments } from '../../organisms/CertificateAttachments/CertificateAttachments.util';
import Select from 'react-select';
import { GET_JOINTS_BY_CERTIFICATE_ID } from '../../../graph-queries/tar/joint';

const DuplicateCertificateForm: FC<{
  certificateId: TCertificate['certificateId'];
  onSubmit: (certificateId: TCertificate['certificateId']) => Promise<unknown>;
  onClose: () => void;
  isLoading?: boolean;
}> = ({ certificateId, onSubmit, onClose, isLoading }) => {
  const { data } = useQuery(GET_CERTIFICATE_DETAILS, { variables: { certificateId } });
  const defaultValues = data?.certificateDetails;

  const { attachments, loadingAttachments, showAll, setShowAll } =
    useCertificateAttachments(certificateId);

  const { data: jointsData, loading: loadingJoints } = useQuery(GET_JOINTS_BY_CERTIFICATE_ID, {
    variables: { certificateId },
  });

  const [checkedConfirmationOptions, setCheckedConfirmationOptions] = React.useState<string[]>([]);
  const confirmationOptions = [
    'The drawing attached is the latest revision',
    'The line spec has been reviewed for any changes',
  ];

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    // Prevent default behavior
    event.preventDefault();

    onSubmit(certificateId)
      .then(onClose)
      .catch((e) => {
        console.error(e);
      });
  };

  const isDuplicateCertificateButtonDisabled = () => {
    if (isLoading || loadingAttachments || loadingJoints) {
      return true;
    }

    // if duplicating from a template, ensure all confirmation checkboxes are checked
    if (defaultValues?.template === true) {
      const allConfirmationChecked = confirmationOptions.length === checkedConfirmationOptions.length;
      return !allConfirmationChecked;
    }

    return false;
  }

  const handleCheckboxChange = (option: string) => {
    setCheckedConfirmationOptions((prev) =>
      prev.includes(option) ? prev.filter((opt) => opt !== option) : [...prev, option]
    );
  }

  return (
    <Form className="row g-3 px-5" onSubmit={handleSubmit}>
      <div className="col-md-6">
        <FormGroup>
          <Label for="certificate-number">Certificate Number</Label>
          <Input disabled id="certificate-number" value={defaultValues?.certificateNumber || ''} />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="maintenance-order">Maintenance Order</Label>
          <Input
            disabled
            id="maintenance-order"
            defaultValue={defaultValues?.maintenanceOrder || ''}
          />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="certificate-title">Title</Label>
          <Input disabled id="certificate-title" value={defaultValues?.title || ''} />
        </FormGroup>
      </div>
      <div className="col-md-6">
        <FormGroup>
          <Label for="certificate-type">Type</Label>
          <Input disabled id="certificate-type" value={defaultValues?.type || ''}></Input>
        </FormGroup>
      </div>

      <div className="col-md-12">
        <FormGroup>
          <Label>Joints</Label>
          <Select
            placeholder="No joints found"
            isDisabled
            value={(jointsData?.jointsByCertificateId || []).map((joint) => ({
              label: joint.jointNumber,
              value: joint.jointId,
            }))}
            isMulti
            components={ReactSelectOverrides.default}
            classNamePrefix="react-select"
          />
        </FormGroup>
      </div>

      <div className="col-md-12">
        <FormGroup>
          <Label>Attachments</Label>
          <div>
            {attachments.length ? (
              <AttachmentsList
                attachments={attachments}
                loading={loadingAttachments}
                showAll={showAll}
                onShowAll={() => setShowAll(true)}
              />
            ) : (
              <Select
                placeholder="No attachments found"
                isDisabled
                components={ReactSelectOverrides.default}
                classNamePrefix="react-select"
              />
            )}
          </div>
        </FormGroup>
      </div>
      
      {defaultValues?.template === true && (
        <div className="col-md-12">
          <Label>Confirm below before duplicating</Label>
          {confirmationOptions.map((option, index) => (
            <FormGroup check key={index} className='my-0'>
              <Input id={`confirmation-checkbox-${index + 1}`} type="checkbox" onChange={() => handleCheckboxChange(option)} />
              <Label for={`confirmation-checkbox-${index + 1}`}>
                {option}
              </Label>
            </FormGroup>
          ))}
        </div>
      )}

      <div className="col-md-12">
        <Button
          type="submit"
          size="lg"
          className="w-100 my-4"
          theme="darker"
          level="primary"
          disabled={isDuplicateCertificateButtonDisabled()}
        >
          Duplicate this certificate
        </Button>
      </div>
    </Form>
  );
};

export default DuplicateCertificateForm;
